import { l } from "../core/i18n.js";
import { loadJSON, saveJSON } from "../core/storage.js";
import { KEYS } from "../../shared/schema.js";
import { toast } from "../core/navigation.js";
import { render, timerDisplay } from "./views.js";
import {
  dateKey,
  ensureTodayStats,
  esc,
  persist,
  studyItemsForToday,
} from "../core/model.js";
import { runtime } from "../core/runtime.js";
import { syncFloatingTimer } from "./floating-timer.js";
import {
  playCompletionChime,
  primeCompletionAudio,
} from "./completion-sound.js";

export function sequenceFlat() {
  const out = [];
  runtime.state.pom.blocks.forEach((b, bi) => {
    const work = Math.max(1, Math.min(600, Number(b.work) || 25)) * 60,
      rest = Math.max(0, Math.min(600, Number(b.rest) || 0)) * 60,
      repeats = Math.max(1, Math.min(100, Number(b.repeats) || 1));
    for (let r = 0; r < repeats; r++)
      out.push({
        work,
        rest,
        block: out.length + 1,
        group: bi + 1,
        repeat: r + 1,
      });
  });
  return out;
}

export function saveActiveTimers() {
  saveJSON(KEYS.activePom, runtime.focusTimer);
  saveJSON(KEYS.activeStudy, runtime.studyTimer);
}

export function restoreActiveTimers() {
  const pom = loadJSON(KEYS.activePom, null);
  if (
    pom &&
    Array.isArray(pom.sequence) &&
    pom.sequence.length &&
    pom.sequence.length <= 10000 &&
    pom.sequence.every(
      (x) =>
        Number.isFinite(x.work) &&
        x.work > 0 &&
        Number.isFinite(x.rest) &&
        x.rest >= 0,
    ) &&
    Number.isInteger(pom.index) &&
    pom.index >= 0 &&
    pom.index < pom.sequence.length &&
    ["work", "rest"].includes(pom.mode)
  ) {
    runtime.focusTimer = pom;
    if (runtime.focusTimer.running) {
      syncPom(Date.now(), true);
      if (runtime.focusTimer.running) {
        clearInterval(startPom.iv);
        startPom.iv = setInterval(tick, 200);
      }
    }
  }
  const study = loadJSON(KEYS.activeStudy, null);
  if (
    study &&
    Array.isArray(study.items) &&
    study.items.length &&
    study.items.length <= 1000 &&
    study.items.every(
      (x) =>
        typeof x.name === "string" &&
        Number.isFinite(x.minutes) &&
        x.minutes > 0 &&
        Number.isFinite(x.workMinutes) &&
        x.workMinutes > 0 &&
        Number.isFinite(x.restMinutes) &&
        x.restMinutes >= 0 &&
        x.workMinutes + x.restMinutes === x.minutes,
    ) &&
    Number.isInteger(study.index) &&
    study.index >= 0 &&
    study.index < study.items.length &&
    ["work", "rest"].includes(study.phase)
  ) {
    runtime.studyTimer = study;
    if (runtime.studyTimer.running) {
      tickStudy(true);
      if (runtime.studyTimer.running) {
        clearInterval(runtime.studyInterval);
        runtime.studyInterval = setInterval(tickStudy, 250);
      }
    }
  }
}

export function startPom() {
  if (runtime.state.prefs.sound) primeCompletionAudio();
  if (runtime.focusTimer?.running) {
    syncPom();
    return;
  }
  const seq = sequenceFlat();
  if (!seq.length) {
    toast("Add a Pomodoro block first.");
    return;
  }
  if (!runtime.focusTimer || runtime.focusTimer.finished) {
    runtime.focusTimer = {
      sequence: seq,
      index: 0,
      mode: "work",
      remaining: seq[0].work * 1000,
      endAt: Date.now() + seq[0].work * 1000,
      running: true,
      paused: false,
      finished: false,
      totalBlocks: seq.length,
      block: 1,
    };
  } else {
    runtime.focusTimer.running = true;
    runtime.focusTimer.paused = false;
    runtime.focusTimer.endAt =
      Date.now() + Math.max(0, runtime.focusTimer.remaining);
  }
  saveActiveTimers();
  clearInterval(startPom.iv);
  startPom.iv = setInterval(tick, 200);
  render();
}

export function tick() {
  if (!runtime.focusTimer || !runtime.focusTimer.running) return;
  if (!syncPom()) updateTimerDOM();
}

export function advanceWork(deadline) {
  const seq = runtime.focusTimer.sequence;
  runtime.focusTimer.index++;
  if (runtime.focusTimer.index >= seq.length) {
    if (runtime.state.pom.loop) runtime.focusTimer.index = 0;
    else {
      runtime.focusTimer.running = false;
      runtime.focusTimer.paused = false;
      runtime.focusTimer.finished = true;
      runtime.focusTimer.remaining = 0;
      runtime.focusTimer.endAt = null;
      clearInterval(startPom.iv);
      return;
    }
  }
  runtime.focusTimer.mode = "work";
  runtime.focusTimer.block = runtime.focusTimer.index + 1;
  runtime.focusTimer.endAt =
    deadline + seq[runtime.focusTimer.index].work * 1000;
}

export function syncPom(now = Date.now(), silent = false) {
  if (!runtime.focusTimer?.running) return false;
  const seq = runtime.focusTimer.sequence;
  if (!Number.isFinite(runtime.focusTimer.endAt))
    runtime.focusTimer.endAt =
      now + Math.max(0, runtime.focusTimer.remaining || 0);
  let changed = false,
    message = "";
  const cycle = seq.reduce((total, x) => total + (x.work + x.rest) * 1000, 0);
  if (
    runtime.state.pom.loop &&
    cycle > 0 &&
    now - runtime.focusTimer.endAt >= cycle
  ) {
    runtime.focusTimer.endAt +=
      Math.floor((now - runtime.focusTimer.endAt) / cycle) * cycle;
    changed = true;
  }
  let guard = seq.length * 2 + 3;
  while (
    runtime.focusTimer.running &&
    runtime.focusTimer.endAt <= now &&
    guard-- > 0
  ) {
    const deadline = runtime.focusTimer.endAt,
      cur = seq[runtime.focusTimer.index];
    if (runtime.focusTimer.mode === "work") {
      if (!silent) recordFocus(cur.work / 60);
      if (cur.rest > 0) {
        runtime.focusTimer.mode = "rest";
        runtime.focusTimer.endAt = deadline + cur.rest * 1000;
        message = l("Work session completed - Rest begins.");
      } else {
        advanceWork(deadline);
        message = runtime.focusTimer.running
          ? l("Work session completed - Next work session begins.")
          : l("Pomodoro sequence complete.");
      }
    } else {
      advanceWork(deadline);
      message = runtime.focusTimer.running
        ? l("Rest completed - Work session begins.")
        : l("Pomodoro sequence complete.");
    }
    changed = true;
  }
  runtime.focusTimer.remaining = runtime.focusTimer.running
    ? Math.max(0, runtime.focusTimer.endAt - now)
    : 0;
  if (changed) {
    saveActiveTimers();
    if (!silent) {
      sound();
      if (message) notify(message);
      if (runtime.focusTimer.finished) toast(message);
      render();
    }
  }
  return changed;
}

export function pausePom() {
  if (!runtime.focusTimer?.running) return;
  syncPom(Date.now(), true);
  if (!runtime.focusTimer.running) {
    render();
    return;
  }
  runtime.focusTimer.remaining = Math.max(
    0,
    runtime.focusTimer.endAt - Date.now(),
  );
  runtime.focusTimer.running = false;
  runtime.focusTimer.paused = true;
  runtime.focusTimer.endAt = null;
  clearInterval(startPom.iv);
  saveActiveTimers();
  render();
}

// Ends the current phase early. Skipped focus time is not counted as focus.
export function skipPom() {
  const f = runtime.focusTimer;
  if (!f || f.finished) return;
  const wasRunning = f.running;
  if (wasRunning) syncPom(Date.now(), true);
  if (!f.finished) {
    const now = Date.now(),
      current = f.sequence[f.index];
    if (f.mode === "work" && current.rest > 0) {
      f.mode = "rest";
      f.endAt = now + current.rest * 1000;
    } else {
      f.running = true;
      advanceWork(now);
    }
    if (!f.finished) {
      f.remaining = Math.max(0, f.endAt - now);
      if (!wasRunning) {
        f.running = false;
        f.paused = true;
        f.endAt = null;
      }
    }
  }
  saveActiveTimers();
  render();
}

export function resetPom() {
  clearInterval(startPom.iv);
  runtime.focusTimer = null;
  saveActiveTimers();
  render();
}

export function recordFocus(minutes) {
  ensureTodayStats();
  const k = dateKey(new Date());
  runtime.state.stats.today.sessions++;
  runtime.state.stats.today.minutes += minutes;
  runtime.state.stats.today.blocks++;
  runtime.state.stats.totalSessions++;
  runtime.state.stats.totalMinutes += minutes;
  runtime.state.stats.totalBlocks++;
  const prev = runtime.state.stats.lastFocusDate;
  if (prev !== k) {
    const y = new Date();
    y.setDate(y.getDate() - 1);
    runtime.state.stats.streak =
      prev === dateKey(y) ? runtime.state.stats.streak + 1 : 1;
    runtime.state.stats.lastFocusDate = k;
  }
  persist();
}

export function updateTimerDOM() {
  const a = document.getElementById("mainTimer"),
    b = document.getElementById("miniTimer");
  if (a) a.textContent = timerDisplay();
  if (b) b.textContent = timerDisplay();
  syncFloatingTimer();
}

export function sound() {
  void playCompletionChime();
}

export function notify(msg) {
  if (runtime.state.pom.notifications && "Notification" in window) {
    if (Notification.permission === "granted") new Notification(msg);
    else if (Notification.permission !== "denied")
      Notification.requestPermission()
        .then((x) => {
          if (x === "granted") new Notification(msg);
        })
        .catch(() => {});
  }
}

export function studyTime(ms) {
  const seconds = Math.max(0, Math.ceil(ms / 1000));
  return `${String(Math.floor(seconds / 3600)).padStart(2, "0")}:${String(Math.floor(seconds / 60) % 60).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}

export function studyChime() {
  void playCompletionChime();
}

export function startStudyFlow() {
  const items = studyItemsForToday();
  if (!items.length) {
    toast(l("Choose at least one study task."));
    return;
  }
  clearInterval(runtime.studyInterval);
  const duration = items[0].workMinutes * 60000;
  runtime.studyTimer = {
    items,
    index: 0,
    phase: "work",
    remaining: duration,
    endAt: Date.now() + duration,
    running: true,
    finished: false,
    minimized: false,
  };
  saveActiveTimers();
  if (runtime.state.prefs.sound) primeCompletionAudio();
  runtime.studyInterval = setInterval(tickStudy, 250);
  render();
  toast(`${l("Study Flow started: ")}${items[0].name}`);
}

export function tickStudy(silent = false) {
  if (!runtime.studyTimer?.running) return;
  const now = Date.now();
  if (!Number.isFinite(runtime.studyTimer.endAt))
    runtime.studyTimer.endAt =
      now + Math.max(0, runtime.studyTimer.remaining || 0);
  let message = "",
    changed = false,
    guard = runtime.studyTimer.items.length * 2 + 1;
  while (
    runtime.studyTimer.running &&
    runtime.studyTimer.endAt <= now &&
    guard-- > 0
  ) {
    message = advanceStudy(runtime.studyTimer.endAt);
    changed = true;
  }
  runtime.studyTimer.remaining = runtime.studyTimer.running
    ? Math.max(0, runtime.studyTimer.endAt - now)
    : 0;
  if (changed) {
    saveActiveTimers();
    if (!silent) {
      studyChime();
      if (message) {
        toast(message);
        notify(`${l("Study Flow: ")}${message}`);
      }
      renderStudyWidget();
    }
  } else updateStudyWidget();
}

export function advanceStudy(deadline = Date.now()) {
  if (!runtime.studyTimer || runtime.studyTimer.finished) return "";
  const current = runtime.studyTimer.items[runtime.studyTimer.index];
  if (runtime.studyTimer.phase === "work" && current.restMinutes > 0) {
    runtime.studyTimer.phase = "rest";
    runtime.studyTimer.endAt = deadline + current.restMinutes * 60000;
    return `${l("Rest time: ")}${current.restMinutes}${l(" min after ")}${current.name}`;
  }
  runtime.studyTimer.index++;
  if (runtime.studyTimer.index >= runtime.studyTimer.items.length) {
    runtime.studyTimer.index = runtime.studyTimer.items.length - 1;
    runtime.studyTimer.remaining = 0;
    runtime.studyTimer.running = false;
    runtime.studyTimer.finished = true;
    runtime.studyTimer.endAt = null;
    clearInterval(runtime.studyInterval);
    return l("Study Flow complete.");
  }
  runtime.studyTimer.phase = "work";
  runtime.studyTimer.endAt =
    deadline +
    runtime.studyTimer.items[runtime.studyTimer.index].workMinutes * 60000;
  return `${l("Next: ")}${runtime.studyTimer.items[runtime.studyTimer.index].name}`;
}

export function skipStudy() {
  if (!runtime.studyTimer || runtime.studyTimer.finished) return;
  const message = advanceStudy(Date.now());
  runtime.studyTimer.remaining = runtime.studyTimer.running
    ? Math.max(0, runtime.studyTimer.endAt - Date.now())
    : 0;
  saveActiveTimers();
  studyChime();
  if (message) {
    toast(message);
    notify(`${l("Study Flow: ")}${message}`);
  }
  renderStudyWidget();
}

export function pauseStudy() {
  if (!runtime.studyTimer?.running) return;
  tickStudy(true);
  if (!runtime.studyTimer.running) {
    renderStudyWidget();
    return;
  }
  runtime.studyTimer.remaining = Math.max(
    0,
    runtime.studyTimer.endAt - Date.now(),
  );
  runtime.studyTimer.running = false;
  runtime.studyTimer.endAt = null;
  clearInterval(runtime.studyInterval);
  saveActiveTimers();
  renderStudyWidget();
}

export function resumeStudy() {
  if (!runtime.studyTimer || runtime.studyTimer.finished) return;
  runtime.studyTimer.running = true;
  runtime.studyTimer.endAt =
    Date.now() + Math.max(0, runtime.studyTimer.remaining);
  saveActiveTimers();
  clearInterval(runtime.studyInterval);
  runtime.studyInterval = setInterval(tickStudy, 250);
  renderStudyWidget();
}

export function closeStudy() {
  clearInterval(runtime.studyInterval);
  runtime.studyTimer = null;
  saveActiveTimers();
  renderStudyWidget();
  if (runtime.page === "pomodoro") render();
}

export function updateStudyWidget() {
  if (!runtime.studyTimer) return;
  const time = document.querySelector("#studyWidget .study-widget-time"),
    bar = document.querySelector("#studyWidget .study-widget-progress i");
  if (time) time.textContent = studyTime(runtime.studyTimer.remaining);
  const peek = document.querySelector("#studyWidget .study-widget-peek");
  if (peek) peek.textContent = studyTime(runtime.studyTimer.remaining);
  if (bar) {
    const item = runtime.studyTimer.items[runtime.studyTimer.index],
      duration =
        (runtime.studyTimer.phase === "rest"
          ? item.restMinutes
          : item.workMinutes) * 60000;
    bar.style.width = `${Math.max(0, Math.min(100, (1 - runtime.studyTimer.remaining / duration) * 100))}%`;
  }
}

export function renderStudyWidget() {
  const host = document.getElementById("studyWidget");
  if (!host) return;
  document.body.classList.toggle("study-active", !!runtime.studyTimer);
  if (!runtime.studyTimer) {
    host.innerHTML = "";
    return;
  }
  const item = runtime.studyTimer.items[runtime.studyTimer.index],
    next = runtime.studyTimer.items[runtime.studyTimer.index + 1],
    resting = runtime.studyTimer.phase === "rest";
  host.innerHTML = `<aside class="study-widget ${runtime.studyTimer.minimized ? "minimized" : ""} ${resting ? "resting" : ""}${l('" aria-label="Study Flow timer"><div class="study-widget-head"><span>')}${runtime.studyTimer.finished ? l("COMPLETE") : resting ? l("REST TIME") : l("FOCUS TIME")} · ${runtime.studyTimer.index + 1}/${runtime.studyTimer.items.length}</span><span class="study-widget-peek">${studyTime(runtime.studyTimer.remaining)}</span><div><button type="button" data-study-minimize aria-label="${runtime.studyTimer.minimized ? l("Expand") : l("Minimize")} ${l("Study Flow")}">${runtime.studyTimer.minimized ? "▢" : "−"}${l('</button><button type="button" data-study-close aria-label="Close Study Flow">×</button></div></div><div class="study-widget-body"><div class="study-widget-task" title="')}${esc(item.name)}">${resting ? l("Rest after ") : ""}${esc(item.name)}</div><div class="study-widget-time">${studyTime(runtime.studyTimer.remaining)}</div><div class="study-widget-progress"><i></i></div><div class="study-widget-next">${runtime.studyTimer.finished ? l("Sequence complete") : resting ? (next ? `${l("Next: ")}${esc(next.name)}` : l("Final rest")) : item.restMinutes ? `${l("Rest: ")}${item.restMinutes}${l(" min")}${next ? `${l(" · Then ")}${esc(next.name)}` : ""}` : next ? `${l("Next: ")}${esc(next.name)}` : l("Final task")}</div><div class="study-widget-actions">${runtime.studyTimer.finished ? "" : `<button type="button" data-study-toggle>${runtime.studyTimer.running ? l("Pause") : l("Resume")}${l('</button><button type="button" data-study-skip>Skip phase →</button>')}`}</div></div></aside>`;
  host.querySelector("[data-study-minimize]").onclick = () => {
    runtime.studyTimer.minimized = !runtime.studyTimer.minimized;
    saveActiveTimers();
    renderStudyWidget();
  };
  host.querySelector("[data-study-close]").onclick = closeStudy;
  host
    .querySelector("[data-study-toggle]")
    ?.addEventListener("click", () =>
      runtime.studyTimer.running ? pauseStudy() : resumeStudy(),
    );
  host.querySelector("[data-study-skip]")?.addEventListener("click", skipStudy);
  updateStudyWidget();
}
